"use client";

/**
 * Purely controlled intake chrome — sidebar (the step rail, or whatever
 * `renderSidebarBody` puts there instead), content area, optional chat footer
 * slot, and Continue footer bar. All navigation state lives in the caller;
 * this component is always a display layer.
 *
 * Used directly by graph-driven containers (e.g. IntakePathBFlow and
 * IntakePathAFlow).
 */

import { type ReactNode, useLayoutEffect, useRef } from "react";
import { ArrowR, Box, Check } from "../lib/icons";
import { INTAKE_CONTINUE_BUTTON_CLASS } from "./intakeFooterStyles";

/** Shared by the chat-composer slot and the Continue-footer slot below, which are mutually exclusive. */
const BOTTOM_BAR_ROW_CLASS =
  "shrink-0 h-[87px] flex items-center bg-(--bg-white) border-t border-(--divider-region) px-8";

/** Marks the current pending prompt (set by IntakePathAFlow / IntakePathBFlow). */
const PROMPT_SCROLL_ANCHOR_SELECTOR = "[data-intake-scroll-anchor]";

/** Gap above the anchored prompt once scrolled to the top. */
const PROMPT_SCROLL_ANCHOR_TOP_GAP_PX = 16;

export interface IntakeStep {
  label: string;
  blurb: string;
}

export interface IntakeLayoutProps {
  // ── Sidebar ───────────────────────────────────────────────────────────────
  header?: ReactNode;
  footer?: ReactNode;
  /** Text under the "Approach" sidebar step. Defaults to "Choose a path". */
  approachBlurb?: string;
  /** When true the approach step shows a checkmark (done state). */
  approachDone?: boolean;
  /** Steps to show in the rail. Empty array → show "steps appear here" placeholder. */
  steps?: IntakeStep[];
  /** Zero-based index of the currently active step. */
  activeStep?: number;
  /**
   * Replaces the step rail entirely, for a surface whose sidebar is not a
   * sequence of steps — the agentic intake reports what the buyer has picked
   * so far there instead. `steps`, `activeStep`, and `showApproachStep` are
   * ignored when this is provided; everything else about the sidebar (header,
   * footer, width, scrolling) is unchanged.
   */
  renderSidebarBody?: () => ReactNode;
  /**
   * When true, the built-in 272px sidebar is not rendered at all — the content
   * pane and bottom bar fill the whole width. For callers that supply their own
   * sidebar chrome outside this component (e.g. a shared app sidebar). Off by
   * default so existing callers are unaffected; `header`/`footer`/
   * `renderSidebarBody`/`steps` are ignored when it is set.
   */
  hideSidebar?: boolean;

  // ── Main content ──────────────────────────────────────────────────────────
  children?: ReactNode;

  // ── Slots ─────────────────────────────────────────────────────────────────
  /**
   * Rendered in the bottom bar. By default it replaces the Continue footer;
   * set `showContinueWithChat` for checkpoints that need both chat and an
   * explicit confirmation action.
   */
  renderChatFooter?: () => ReactNode | null;
  showContinueWithChat?: boolean;

  /** Absolutely-positioned right-rail overlay (citation panel etc.). */
  renderRail?: ReactNode;
  /** Changes when chat history or its active prompt changes, scrolling the content to its latest item. */
  chatScrollKey?: string;

  // ── Footer navigation ─────────────────────────────────────────────────────
  /**
   * Continue button click handler. Omit to hide the footer bar. It is shown
   * alongside a chat footer only when `showContinueWithChat` is enabled.
   */
  onContinue?: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
  busy?: boolean;
}

export function IntakeLayout({
  header,
  footer,
  // approachBlurb intentionally unused — step rail is single-line now.
  approachDone = false,
  steps = [],
  activeStep = 0,
  renderSidebarBody,
  hideSidebar = false,
  children,
  renderChatFooter,
  showContinueWithChat = false,
  renderRail,
  chatScrollKey,
  onContinue,
  continueLabel = "Continue",
  continueDisabled,
  busy,
}: IntakeLayoutProps) {
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const scrollSpacerRef = useRef<HTMLDivElement>(null);
  const chatNode = renderChatFooter?.();
  const showFooter = onContinue !== undefined && !chatNode;

  // Scroll to the top of the pending prompt if one is marked, otherwise to
  // the container bottom (e.g. status/stall notices, done state).
  useLayoutEffect(() => {
    if (chatScrollKey === undefined) return;
    const content = contentScrollRef.current;
    if (!content) return;
    const spacer = scrollSpacerRef.current;
    // Reset before measuring so a shorter prompt doesn't inherit stale space.
    if (spacer) spacer.style.height = "0px";

    const anchor = content.querySelector<HTMLElement>(
      PROMPT_SCROLL_ANCHOR_SELECTOR,
    );
    if (!anchor) {
      content.scrollTop = content.scrollHeight;
      return;
    }
    const anchorTop =
      anchor.getBoundingClientRect().top -
      content.getBoundingClientRect().top +
      content.scrollTop;
    const target = Math.max(0, anchorTop - PROMPT_SCROLL_ANCHOR_TOP_GAP_PX);

    // Grow the trailing spacer just enough for `target` to be reachable —
    // otherwise the browser clamps scrollTop to scrollHeight - clientHeight.
    const maxScroll = content.scrollHeight - content.clientHeight;
    if (spacer && target > maxScroll) {
      spacer.style.height = `${target - maxScroll}px`;
    }

    content.scrollTop = target;
  }, [chatScrollKey]);

  function stepState(i: number): "done" | "on" | "up" {
    if (activeStep > i) return "done";
    if (activeStep === i) return "on";
    return "up";
  }

  return (
    <div className="h-full flex bg-(--bg-canvas) min-h-0 font-[family-name:var(--font-display)]">
      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      {!hideSidebar && (
        <aside className="shrink-0 w-[272px] bg-(--bg-white) border-r border-(--divider-region) flex flex-col p-4 overflow-y-auto">
          {header ?? (
            <div>
              <div className="text-[10.5px] tracking-[.1em] uppercase text-(--fg-muted) font-semibold">
                New ROI opportunity
              </div>
              <div className="text-[19px] font-bold tracking-[-0.02em] text-(--fg-primary) mt-[5px]">
                Set up your model
              </div>
            </div>
          )}

          {renderSidebarBody ? (
            <div className="mt-[26px]">{renderSidebarBody()}</div>
          ) : (
            <ol className="list-none m-0 p-0 mt-[26px] flex flex-col gap-0.5">
              {/* Approach step */}
              <li
                className={[
                  "relative flex items-start gap-3 p-3 rounded-(--radius-sm)",
                  !approachDone ? "bg-(--bg-sky)" : "",
                ].join(" ")}
              >
                <span
                  className="absolute left-[23px] top-[34px] bottom-[-2px] w-[1.5px] bg-(--border-subtle)"
                  aria-hidden="true"
                />
                <span
                  className={[
                    "relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold shrink-0 border-[1.5px]",
                    approachDone
                      ? "bg-(--prov) border-(--prov) text-white"
                      : "bg-(--brand-navy) border-(--brand-navy) text-white",
                  ].join(" ")}
                >
                  {approachDone ? <Check size={13} /> : <Box size={13} />}
                </span>
                <span className="flex items-center pt-0.5">
                  <span className="text-[13.5px] font-semibold text-(--fg-primary)">
                    Approach
                  </span>
                </span>
              </li>

              {/* Step rail */}
              {steps.length > 0 ? (
                steps.map((s, i) => {
                  const st = stepState(i);
                  const isLast = i === steps.length - 1;
                  return (
                    <li
                      key={i}
                      className={[
                        "relative flex items-start gap-3 p-3 rounded-(--radius-sm)",
                        st === "on" ? "bg-(--bg-sky)" : "",
                        st === "up" ? "opacity-55" : "",
                      ].join(" ")}
                    >
                      {!isLast && (
                        <span
                          className="absolute left-[23px] top-[34px] bottom-[-2px] w-[1.5px] bg-(--border-subtle)"
                          aria-hidden="true"
                        />
                      )}
                      <span
                        className={[
                          "relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold shrink-0 border-[1.5px]",
                          st === "done"
                            ? "bg-(--prov) border-(--prov) text-white"
                            : st === "on"
                              ? "bg-(--brand-navy) border-(--brand-navy) text-white"
                              : "bg-(--bg-white) border-(--border-default) text-(--fg-muted)",
                        ].join(" ")}
                      >
                        {st === "done" ? <Check size={13} /> : i + 1}
                      </span>
                      <span className="flex items-center pt-0.5">
                        <span
                          className={[
                            "text-[13.5px]",
                            st === "on"
                              ? "font-semibold text-(--fg-primary)"
                              : st === "up"
                                ? "font-medium text-(--fg-muted)"
                                : "font-semibold text-(--fg-primary)",
                          ].join(" ")}
                        >
                          {s.label}
                        </span>
                      </span>
                    </li>
                  );
                })
              ) : (
                <li className="relative flex items-start gap-3 p-3 opacity-55">
                  <span className="relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-[14px] text-(--fg-muted) bg-(--bg-white) border-[1.5px] border-(--border-default) shrink-0">
                    ·
                  </span>
                  <span className="flex flex-col gap-0.5 pt-0.5">
                    <span className="text-[13.5px] font-medium text-(--fg-muted)">
                      Steps appear here
                    </span>
                    <span className="text-[11.5px] text-(--fg-muted)">
                      once you choose a path
                    </span>
                  </span>
                </li>
              )}
            </ol>
          )}

          {footer && (
            <>
              <div className="flex-1" />
              {footer}
            </>
          )}
        </aside>
      )}

      {/* ── Main content ────────────────────────────────────────────────── */}
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        <div
          ref={contentScrollRef}
          data-testid="intake-content-scroll"
          className="min-h-0 flex-1 overflow-y-auto"
        >
          <div className="w-full min-h-full px-8 pt-[26px] pb-9">
            {children}
          </div>
          {/* Grown by the scroll effect so short prompts can reach the top gap. */}
          <div
            ref={scrollSpacerRef}
            data-testid="intake-scroll-spacer"
            aria-hidden="true"
          />
        </div>

        {/* Chat composer slot */}
        {chatNode && (
          <div className={BOTTOM_BAR_ROW_CLASS}>
            <div className="mx-auto flex w-full max-w-[680px] items-center gap-3">
              <div className="min-w-0 flex-1">{chatNode}</div>
              {showContinueWithChat && onContinue && (
                <button
                  type="button"
                  disabled={continueDisabled || busy}
                  className={INTAKE_CONTINUE_BUTTON_CLASS}
                  onClick={onContinue}
                >
                  {continueLabel} <ArrowR size={16} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Footer navigation bar */}
        {showFooter && (
          <div className={BOTTOM_BAR_ROW_CLASS}>
            <div className="mx-auto w-full max-w-[680px] flex items-center gap-3">
              <span className="flex-1" />
              {onContinue && (
                <button
                  type="button"
                  disabled={continueDisabled || busy}
                  className={INTAKE_CONTINUE_BUTTON_CLASS}
                  onClick={onContinue}
                >
                  {continueLabel} <ArrowR size={16} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Right rail overlay */}
        {renderRail}
      </div>
    </div>
  );
}
